/**
 * R-77 — per-section provider relevance verifier.
 */
import type { ReportTypeId } from '../../config/reportTypes';
import { getSectionsForType } from '../../config/reportSections';
import type { DataSource, SiteRagReport } from '../../types/siteRag';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import { PROVIDER_TO_SED_SECTION } from './providerCoverageGate';
import {
  getProviderRelevanceRules,
  normalizeAllowedList,
  type ProviderRelevanceRule,
} from './providerRelevanceRegistry';
import { normalizeProviderId } from './providerCoverageRegistry';
import { extractSections, flattenToText, reportFullText } from './reportTextExtract';

export type ProviderRelevanceViolationKind =
  | 'IRRELEVANT_PROVIDER'
  | 'SOLE_NUMERIC_UNSUPPORTED'
  | 'MISSING_PREFERRED';

export interface ProviderRelevanceFinding {
  kind: ProviderRelevanceViolationKind;
  severity: 'block' | 'warn' | 'info';
  reportTypeId: string;
  sectionId: string;
  sectionTitle?: string;
  providerId: string;
  refIndex?: number;
  message: string;
  excerpt?: string;
  lineNumber?: number;
}

export interface EvidenceChunk {
  providerId: string;
  text: string;
  sectionId?: string;
  [key: string]: unknown;
}

const REF_TOKEN_RE = /\[Ref\s+(\d+)\]/gi;

const NUMERIC_CLAIM_RE = /\b[\d,]+(?:\.\d+)?\s*(?:%|MW|GW|km|mi|miles|kilometers|acres|kV|mg\/L|ppm|°F|°C|\$|USD|GWh|MWh|cfs|gpm)\b/i;

const FIELD_TO_PROVIDER: Record<string, string> = {
  grid: 'hifld-grid',
  electricity: 'eia-electricity',
  environmental: 'usfws-env',
  flood: 'fema-flood',
  climate: 'open-meteo',
  seismic: 'usgs-seismic',
  airQuality: 'airnow-aqi',
  waterStress: 'wri-aqueduct',
  solar: 'nrel-solar',
  wind: 'nrel-wind',
  nuclearInfra: 'nrc-reactors',
  connectivity: 'peeringdb-ix',
  siteResearch: 'site-research',
  parcel: 'regrid-parcels',
  publicSentiment: 'public-sentiment',
  externalLiterature: 'external-literature',
};

function normalizeHeading(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '');
}

function splitMarkdownByH2(markdown: string): Array<{ id: string; title: string; content: string }> {
  const sections: Array<{ id: string; title: string; content: string }> = [];
  const parts = markdown.split(/^##\s+/m);
  if (parts.length <= 1) return sections;
  for (let i = 1; i < parts.length; i += 1) {
    const chunk = parts[i];
    const nl = chunk.indexOf('\n');
    const title = (nl >= 0 ? chunk.slice(0, nl) : chunk).trim();
    const content = nl >= 0 ? chunk.slice(nl + 1) : '';
    const id = normalizeHeading(title).slice(0, 64) || `h2-${i}`;
    sections.push({ id, title, content: content.trim() });
  }
  return sections;
}

function resolveReportSections(report: SiteRagReport): Array<{ id: string; title: string; content: string }> {
  const reportTypeId = (report.reportType ?? 'feasibility') as ReportTypeId;
  const r = report as unknown as Record<string, unknown>;
  const registrySections = getSectionsForType(reportTypeId);
  const byRegistry: Array<{ id: string; title: string; content: string }> = [];

  for (const entry of registrySections) {
    if (entry.sectionId === 'toc' || entry.sectionId === 'blindSpots' || entry.sectionId === 'appendix') {
      continue;
    }
    let val = r[entry.sectionId];
    if (report.customBriefAnswers?.length && entry.sectionId === 'projectContext') {
      val = report.customBriefAnswers
        .map((a) => [a.question, a.findings, a.evidence].filter(Boolean).join('\n'))
        .join('\n\n');
    }
    if (typeof val === 'string' && val.trim()) {
      byRegistry.push({ id: entry.sectionId, title: entry.defaultLabel, content: val });
    } else if (val != null && typeof val === 'object') {
      const flat = flattenToText(val);
      if (flat.trim()) {
        byRegistry.push({ id: entry.sectionId, title: entry.defaultLabel, content: flat });
      }
    }
  }

  const fieldSections = extractSections(report).map((s) => ({
    id: s.id,
    title: s.id,
    content: s.content,
  }));
  const md = (report as SiteRagReport & { markdown?: string }).markdown?.trim() ?? '';
  if (md.length > 100) {
    const h2Sections = splitMarkdownByH2(md);
    if (h2Sections.length >= byRegistry.length && h2Sections.length >= fieldSections.length) {
      return h2Sections;
    }
  }
  if (byRegistry.length > 0) return byRegistry;
  if (fieldSections.length > 0) return fieldSections;
  if (md.length > 0) return [{ id: 'markdown', title: 'markdown', content: md }];
  return [{ id: 'fullReport', title: 'fullReport', content: reportFullText(report) }];
}

/** Resolve whitelist rule for a section — unknown sections return null (skip). */
export function resolveSectionRule(
  reportType: ReportTypeId,
  sectionTitle: string,
  sectionId?: string,
): ProviderRelevanceRule | null {
  if (reportType === 'custom-brief') return null;

  const typeRules = getProviderRelevanceRules(reportType);
  if (!typeRules || Object.keys(typeRules).length === 0) return null;

  if (sectionId && typeRules[sectionId]) return typeRules[sectionId];

  const sections = getSectionsForType(reportType);
  const titleNorm = normalizeHeading(sectionTitle);
  for (const s of sections) {
    if (typeRules[s.sectionId] && normalizeHeading(s.defaultLabel) === titleNorm) {
      return typeRules[s.sectionId];
    }
  }

  if (sectionId) {
    for (const s of sections) {
      if (s.sectionId === sectionId && typeRules[s.sectionId]) {
        return typeRules[s.sectionId];
      }
    }
  }

  return null;
}

/** Resolve orchestrator provider id from a references[] row. */
export function resolveProviderFromReference(ref: DataSource | undefined): string | null {
  if (!ref) return null;

  const source = (ref.source ?? '').trim();
  const upper = source.toUpperCase();
  const field = (ref.field ?? '').trim();

  if (field && FIELD_TO_PROVIDER[field]) {
    return normalizeProviderId(FIELD_TO_PROVIDER[field]!);
  }

  for (const orchestratorId of Object.keys(PROVIDER_TO_SED_SECTION)) {
    const needle = orchestratorId.replace(/-/g, ' ').toUpperCase();
    if (upper.includes(orchestratorId.toUpperCase()) || upper.includes(needle)) {
      return normalizeProviderId(orchestratorId);
    }
  }

  if (upper.includes('HIFLD')) return 'hifld-grid';
  if (upper.includes('FEMA') || upper.includes('NFHL')) return 'fema-flood';
  if (upper.includes('USFWS') || upper.includes('IPAC') || upper.includes('NWI')) return 'usfws-env';
  if (upper.includes('NRC') || upper.includes('ADAMS')) return 'nrc-reactors';
  if (upper.includes('PEERING')) return 'peeringdb-ix';
  if (upper.includes('NREL')) return upper.includes('WIND') ? 'nrel-wind' : 'nrel-solar';
  if (upper.includes('USGS')) return 'usgs-seismic';
  if (upper.includes('EIA')) return 'eia-electricity';
  if (upper.includes('WRI') || upper.includes('AQUEDUCT')) return 'wri-aqueduct';
  if (upper.includes('AIRNOW') || upper.includes('EPA AQS')) return 'airnow-aqi';
  if (upper.includes('OPEN-METEO') || upper.includes('OPEN METEO')) return 'open-meteo';
  if (upper.includes('REGRID')) return 'regrid-parcels';
  if (upper.includes('SENTIMENT')) return 'public-sentiment';

  return null;
}

/** True when surrounding text carries a load-bearing numeric claim. */
export function isNumericClaim(textAroundCite: string): boolean {
  return NUMERIC_CLAIM_RE.test(textAroundCite);
}

function extractSentence(text: string, refPos: number): string {
  const before = text.slice(0, refPos);
  const after = text.slice(refPos);
  const start = Math.max(
    before.lastIndexOf('. ') + 2,
    before.lastIndexOf('.\n') + 2,
    before.lastIndexOf('\n') + 1,
    0,
  );
  const endRel = after.search(/[.!?](?:\s|$)/);
  const end = endRel >= 0 ? refPos + endRel + 1 : text.length;
  return text.slice(start, end).trim();
}

function countRefsInSentence(sentence: string): number {
  const matches = sentence.match(/\[Ref\s+\d+\]/gi);
  return matches?.length ?? 0;
}

function contextAroundRef(text: string, matchIndex: number, matchLen: number, window = 120): string {
  const start = Math.max(0, matchIndex - window);
  const end = Math.min(text.length, matchIndex + matchLen + window);
  return text.slice(start, end);
}

function parseRefIndices(content: string): Array<{ index: number; pos: number; raw: string }> {
  const out: Array<{ index: number; pos: number; raw: string }> = [];
  REF_TOKEN_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = REF_TOKEN_RE.exec(content)) !== null) {
    const n = parseInt(m[1]!, 10);
    if (Number.isFinite(n) && n >= 1) {
      out.push({ index: n, pos: m.index, raw: m[0] });
    }
  }
  return out;
}

function isAllowed(providerId: string, rule: ProviderRelevanceRule): boolean {
  const allowed = normalizeAllowedList(rule.allowed);
  return allowed.includes(normalizeProviderId(providerId));
}

function isPreferred(providerId: string, rule: ProviderRelevanceRule): boolean {
  const preferred = normalizeAllowedList(rule.preferred);
  return preferred.includes(normalizeProviderId(providerId));
}

export function verifyProviderRelevance(
  report: SiteRagReport,
  _evidence?: SiteEvidenceDocument | null,
): ProviderRelevanceFinding[] {
  const reportTypeId = (report.reportType ?? 'feasibility') as ReportTypeId;
  if (reportTypeId === 'custom-brief') return [];

  const refs = report.references ?? [];
  const findings: ProviderRelevanceFinding[] = [];
  const sections = resolveReportSections(report);

  for (const section of sections) {
    if (section.id === 'executiveSummary' || section.id === 'executive') continue;

    const rule = resolveSectionRule(reportTypeId, section.title, section.id);
    if (!rule) continue;

    const refsInSection = parseRefIndices(section.content);
    if (refsInSection.length === 0) continue;

    const providersUsed = new Set<string>();
    let hasPreferred = false;

    for (const cite of refsInSection) {
      const refRow = refs[cite.index - 1];
      const providerId = resolveProviderFromReference(refRow);
      if (!providerId) continue;

      providersUsed.add(providerId);
      if (isPreferred(providerId, rule)) hasPreferred = true;

      if (isAllowed(providerId, rule)) continue;

      const excerpt = contextAroundRef(section.content, cite.pos, cite.raw.length);
      const sentence = extractSentence(section.content, cite.pos);
      const soleRef = countRefsInSentence(sentence) <= 1;
      const numeric = isNumericClaim(sentence);

      const severity: ProviderRelevanceFinding['severity'] =
        soleRef && numeric ? 'block' : 'warn';

      findings.push({
        kind: soleRef && numeric ? 'SOLE_NUMERIC_UNSUPPORTED' : 'IRRELEVANT_PROVIDER',
        severity,
        reportTypeId,
        sectionId: section.id,
        sectionTitle: section.title,
        providerId,
        refIndex: cite.index,
        message:
          severity === 'block'
            ? `Provider "${providerId}" is not allowed in section "${section.title}" but is sole support for a numeric claim [Ref ${cite.index}].`
            : `Provider "${providerId}" is not in the allowed list for section "${section.title}" [Ref ${cite.index}].`,
        excerpt: excerpt.slice(0, 240),
        lineNumber: section.content.slice(0, cite.pos).split('\n').length,
      });
    }

    if (refsInSection.length > 0 && !hasPreferred && rule.preferred.length > 0) {
      findings.push({
        kind: 'MISSING_PREFERRED',
        severity: 'info',
        reportTypeId,
        sectionId: section.id,
        sectionTitle: section.title,
        providerId: rule.preferred[0] ?? '',
        message: `Section "${section.title}" cites no preferred providers (${rule.preferred.join(', ')}).`,
      });
    }
  }

  return findings;
}

/** Read-only filter — drops evidence chunks whose provider is not allowed for the section. */
export function stripIrrelevantEvidence(
  reportTypeId: ReportTypeId,
  sectionId: string,
  evidenceChunks: EvidenceChunk[],
): EvidenceChunk[] {
  const rule = getProviderRelevanceRules(reportTypeId)[sectionId];
  if (!rule) return evidenceChunks;

  const allowed = new Set(normalizeAllowedList(rule.allowed));
  return evidenceChunks.filter((chunk) => allowed.has(normalizeProviderId(chunk.providerId)));
}

export interface ProviderRelevanceGateOptions {
  siteId?: string;
  reportId?: string;
}

export function applyProviderRelevanceGate(
  report: SiteRagReport,
  sed?: SiteEvidenceDocument | null,
  _opts?: ProviderRelevanceGateOptions,
): SiteRagReport {
  const findings = verifyProviderRelevance(report, sed);
  if (findings.length === 0) return report;

  const blocks = findings.filter((f) => f.severity === 'block').length;
  const warns = findings.filter((f) => f.severity === 'warn').length;
  console.info('[R-77 provider relevance]', {
    reportId: report.id,
    blocks,
    warns,
    info: findings.length - blocks - warns,
  });

  return {
    ...report,
    qaFindings: {
      ...(report.qaFindings ?? {}),
      providerRelevance: findings,
    },
  };
}
